import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sponsorsApi, type Sponsor } from "@/integrations/mysql/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus, GripVertical, Eye, EyeOff, ExternalLink } from "lucide-react";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY_FORM = {
  name: "", logo_url: "", link_url: "", description: "", is_active: true, display_order: 0,
};

// ─── Sortable row ──────────────────────────────────────────────────────────
function SortableSponsor({
  sponsor, onEdit, onDelete, onToggle, disabled,
}: {
  sponsor: Sponsor;
  onEdit: (s: Sponsor) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, cur: boolean) => void;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sponsor.id });

  return (
    <Card
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors shrink-0">
            <GripVertical className="w-5 h-5" />
          </div>

          {/* Logo preview */}
          {sponsor.logo_url ? (
            <img src={sponsor.logo_url} alt={sponsor.name} className="w-12 h-10 object-contain rounded-lg border border-gray-100 bg-gray-50 shrink-0" />
          ) : (
            <div className="w-12 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-lg shrink-0 select-none">🤝</div>
          )}

          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-800 truncate">{sponsor.name}</p>
            {sponsor.description && <p className="text-xs text-gray-400 truncate">{sponsor.description}</p>}
            {sponsor.link_url && (
              <a href={sponsor.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-primary flex items-center gap-1 hover:underline mt-0.5 truncate">
                <ExternalLink className="w-3 h-3 shrink-0" /> {sponsor.link_url}
              </a>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => onToggle(sponsor.id, sponsor.is_active)}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                sponsor.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
              }`}
            >
              {sponsor.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {sponsor.is_active ? "Visível" : "Oculto"}
            </button>
            <Button size="sm" variant="outline" onClick={() => onEdit(sponsor)} disabled={disabled} className="h-7 w-7 p-0">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(sponsor.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:border-red-300">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export function SponsorsManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: ["sponsors-admin"],
    queryFn: () => sponsorsApi.list(false),
  });

  const [localItems, setLocalItems] = useState<Sponsor[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const displayed = localItems ?? sponsors;

  const createMutation = useMutation({
    mutationFn: (data: typeof EMPTY_FORM) => sponsorsApi.create({ ...data, display_order: displayed.length }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["sponsors-admin"] }); queryClient.invalidateQueries({ queryKey: ["sponsors-public"] }); toast({ title: "Patrocinador criado!" }); },
    onError: () => toast({ title: "Erro ao criar", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Sponsor> }) => sponsorsApi.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["sponsors-admin"] }); queryClient.invalidateQueries({ queryKey: ["sponsors-public"] }); toast({ title: "Salvo!" }); },
    onError: () => toast({ title: "Erro ao salvar", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: sponsorsApi.delete,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["sponsors-admin"] }); queryClient.invalidateQueries({ queryKey: ["sponsors-public"] }); setLocalItems(null); toast({ title: "Excluído!" }); },
    onError: () => toast({ title: "Erro ao excluir", variant: "destructive" }),
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = displayed.findIndex(s => s.id === active.id);
    const newIdx = displayed.findIndex(s => s.id === over.id);
    const reordered = arrayMove(displayed, oldIdx, newIdx);
    setLocalItems(reordered);
    try {
      await Promise.all(reordered.map((s, i) => sponsorsApi.update(s.id, { display_order: i })));
      queryClient.invalidateQueries({ queryKey: ["sponsors-public"] });
      toast({ title: "Ordem atualizada!" });
    } catch {
      toast({ title: "Erro ao reordenar", variant: "destructive" });
      setLocalItems(null);
    }
  };

  const resetForm = () => setForm({ ...EMPTY_FORM, display_order: displayed.length });
  const handleEdit = (s: Sponsor) => {
    setEditingId(s.id);
    setForm({ name: s.name, logo_url: s.logo_url ?? "", link_url: s.link_url ?? "", description: s.description ?? "", is_active: s.is_active, display_order: s.display_order });
    setIsCreating(false);
  };

  const handleSave = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: form });
      setEditingId(null);
    } else {
      createMutation.mutate(form);
      setIsCreating(false);
    }
    resetForm();
    setLocalItems(null);
  };

  const handleCancel = () => { setEditingId(null); setIsCreating(false); resetForm(); };
  const isValid = form.name.trim().length >= 2;

  if (isLoading) return <div className="p-4 text-sm text-gray-400">Carregando patrocinadores...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setIsCreating(true); resetForm(); }} disabled={isCreating || !!editingId}>
          <Plus className="w-4 h-4 mr-2" /> Novo Patrocinador
        </Button>
      </div>

      {(isCreating || editingId) && (
        <Card className="border-2 border-brand-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{editingId ? "Editar Patrocinador" : "Novo Patrocinador"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Nome *</Label>
                <Input className="mt-1" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Clínica Parceira XYZ" maxLength={200} />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Link (URL)</Label>
                <Input className="mt-1" value={form.link_url} onChange={e => setForm(p => ({ ...p, link_url: e.target.value }))} placeholder="https://exemplo.com.br" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Descrição (opcional)</Label>
              <Textarea className="mt-1" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} maxLength={300} placeholder="Breve descrição do parceiro..." />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600 block mb-2">Logo / Foto</Label>
              <PhotoCropUpload
                value={form.logo_url}
                onChange={url => setForm(p => ({ ...p, logo_url: url }))}
                shape="square"
                aspect={3 / 2}
                label="Logo do patrocinador"
                previewClassName="w-24 h-16 rounded-xl object-contain"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} />
              <Label>Visível no carrossel</Label>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={!isValid}>Salvar</Button>
              <Button variant="outline" onClick={handleCancel}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {displayed.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">Nenhum patrocinador cadastrado. Clique em "Novo Patrocinador" para começar.</div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" />Arraste para reordenar</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={displayed.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {displayed.map(s => (
                <SortableSponsor key={s.id} sponsor={s} onEdit={handleEdit} onDelete={id => setDeleteId(id)}
                  onToggle={(id, cur) => updateMutation.mutate({ id, data: { is_active: !cur } })} disabled={!!editingId || isCreating} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir patrocinador</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza? Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
